import type { ITopicEntity } from '@1js/cortex-topics-types';
import classnamesBind from 'classnames/bind';
import { observer } from 'mobx-react-lite';
import type { ClientItem } from 'owa-mail-store';
import * as React from 'react';
import TopicCardWrapper from './TopicCardWrapper';
import styles from './TopicDropdownItem.scss';

const classNames = classnamesBind.bind(styles);

export interface BaseDropdownItemProps {
    topic?: ITopicEntity;
    item?: ClientItem;
    /* If set, this is used as the highlighted name for the topic card, overriding the topic name */
    highlightedName?: string;
    /* If set, onClick handler for the whole item. Better to *not* use with showTopicCard */
    onClick?: (topic: ITopicEntity) => void;
    /* Changes the background color to denote this topic is highlighted */
    isHighlighted?: boolean;
    /* If true, the item will go darker on hover */
    withHover?: boolean;
    /* If true, shows topic card on hover */
    showTopicCard?: boolean;
    /* Highlights the text and changes the styles to denote this topic was added manually */
    isManual?: boolean;
    isFeedbackDisabled?: boolean;
    children?: React.ReactNode;
}

/**
 * Re-usable control for an entry in a dropdown. Used in the topic roll-up and the manual topic annotations.
 */
export default observer(function BaseDropdownItem(props: BaseDropdownItemProps) {
    const onClickCallback = () => props.onClick?.(props.topic);
    const item = (
        <div
            className={classNames(
                styles.topicCard,
                props.isHighlighted
                    ? styles.selectedTopic
                    : props.withHover
                    ? styles.withHover
                    : undefined
            )}
            onClick={onClickCallback}>
            {props.children}
        </div>
    );
    return props.showTopicCard ? (
        <TopicCardWrapper
            emailAddress={undefined}
            name={props.topic.DisplayName}
            topicId={props.topic.Id}
            personaType="Topic"
            clientScenario="TopicAnnotation"
            topicMetadata={{
                highlightedText: props.highlightedName || props.topic.DisplayName,
                hideFeedbackQuestions: props.isManual || props.isFeedbackDisabled,
                source: props.isManual ? 'ManualRollup' : 'AutomaticRollup',
            }}
            cardPlacementMode="AnchorSideWithBeak">
            {item}
        </TopicCardWrapper>
    ) : (
        item
    );
});
